import axios from "axios";
import { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Container,
  Form,
  FormSelect,
  FormTextarea,
  Row,
} from "shards-react";
import { HpTabs, HpTab } from "../HpTabs/HpTabs";
import { HpButton } from "../HpButton/HpButton";
import Cookies from "universal-cookie";
import API from "../Api/API";
import authService from "../AuthService/AuthService";
import { handleErrorMessage } from "../ErrorMessages/ErrorMessages";
import { showNotification } from "../Notification/Notification";
import HpModal from "../Modal/HpModal";
import SectionTitle from "../SectionTitle/SectionTitle";

const MessageForm = ({
  primaryColor,
  isClubManager,
  embedded,
  onClose,
  open,
  toggle,
}) => {
  const { i18n, t } = useTranslation(["message"]);

  const [users, setUsers] = useState([]);
  const [squads, setSquads] = useState([]);
  const [club, setClub] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [showClub, setShowClub] = useState(false);
  const [messageFor, setMessageFor] = useState("user");

  useEffect(() => {
    const getData = async () => {
      const cookies = new Cookies();
      const clubId = cookies.get("selectedClubId");

      let response = await fetchAllUsers(clubId);
      response = response.sort((a, b) => {
        return (a.lastName + " " + a.firstName).localeCompare(
          b.lastName + " " + b.firstName,
          i18n.language,
        );
      });
      setUsers(response);

      response = await fetchAllSquads(clubId);
      response = response.sort((a, b) => {
        return a.name.localeCompare(b.name, i18n.language);
      });
      setSquads(response);

      response = await fetchClub(clubId);
      setClub(response);
    };
    getData();
  }, []);

  useEffect(() => {
    const userId = authService.getUserId();
    let found = false;
    if (club && club.managers) {
      for (const element of club.managers) {
        if (isClubManager && element.id === parseInt(userId)) {
          setShowClub(true);
          found = true;
          break;
        }
      }
    }
    if (!found) {
      setShowClub(false);
    }
  }, [club, isClubManager]);

  const fetchAllUsers = (clubId) => {
    return axios
      .get(API.url + "api/user/all-for-user/" + clubId, {
        withCredentials: true,
      })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        handleErrorMessage(err.response.status);
      });
  };

  const fetchAllSquads = (clubId) => {
    return axios
      .get(API.url + "api/squad/all-for-user/" + clubId, {
        withCredentials: true,
      })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        handleErrorMessage(err.response.status);
      });
  };

  const fetchClub = async (id) => {
    return axios
      .get(API.url + "api/club/" + id, {
        withCredentials: true,
      })
      .then((res) => res.data)
      .catch((error) => handleErrorMessage(error.response.status));
  };

  const closeDialog = (e) => {
    if (e) e.preventDefault();
    if (onClose) {
      onClose();
    }
    document.getElementById("message_input").value = "";
    if (document.getElementById("for_input")) {
      document.getElementById("for_input").value = 0;
    }
    setMessageFor("user");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setDisabled(true);
    const text = document.getElementById("message_input").value;

    const cookies = new Cookies();
    const clubId = cookies.get("selectedClubId");

    let userId = 0,
      squadId = 0;
    if (messageFor === "user") {
      userId = document.getElementById("for_input").value;
      if (userId === 0) {
        return;
      }
    } else if (messageFor === "squad") {
      squadId = document.getElementById("for_input").value;
      if (squadId === 0) {
        return;
      }
    }

    const payload = {
      text: text,
      userId: userId,
      squadId: squadId,
      clubId: clubId,
    };

    axios
      .post(API.url + "api/mail/send", payload, { withCredentials: true })
      .then(() => {
        closeDialog(e);
        if (toggle) toggle();
        setDisabled(false);
        document.getElementById("message_input").value = "";
        showNotification(t("message_sent_successfully"));
      })
      .catch((error) => handleErrorMessage(error.response.status));
  };

  const formBody = (
    <Form onSubmit={handleSubmit} id="message-form-embedded">
      <Row className="mb-3">
        <Col lg={{ size: 10, offset: 1 }}>
          <HpTabs value={messageFor} onChange={setMessageFor}>
            <HpTab value="user">{t("user")}</HpTab>
            <HpTab value="squad">{t("squad")}</HpTab>
            {showClub && <HpTab value="club">{t("club")}</HpTab>}
          </HpTabs>
        </Col>
      </Row>
      {(messageFor === "user" || messageFor === "squad") && (
        <Row>
          <Col lg={{ size: 10, offset: 1 }}>
            <FormSelect
              id={"for_input"}
              className="ml-0 mb-1"
              style={{ borderRadius: "0.375rem", margin: 0, width: "100%" }}
            >
              {messageFor === "user" && [
                <option key="select_user" value={0}>
                  {t("select_user")}
                </option>,
                users.map((user) => (
                  <option value={user.id} key={"user_" + user.id}>
                    {user.lastName + " " + user.firstName}
                  </option>
                )),
              ]}
              {messageFor === "squad" && [
                <option key="select_squad" value={0}>
                  {t("select_squad")}
                </option>,
                squads.map((squad) => (
                  <option value={squad.id} key={"squad_" + squad.id}>
                    {squad.name}
                  </option>
                )),
              ]}
            </FormSelect>
          </Col>
        </Row>
      )}
      <Row className="mb-3">
        <Col lg={{ size: 10, offset: 1 }}>
          <FormTextarea
            id={"message_input"}
            className={"overall_review_input"}
            placeholder={t("message")}
          />
        </Col>
      </Row>
    </Form>
  );

  if (embedded) {
    return <Container>{formBody}</Container>;
  }

  return (
    <HpModal
      open={open}
      toggle={toggle}
      title={
        <SectionTitle
          level={3}
          className="mb-0"
          primaryColor={primaryColor}
          tooltipId="message_info"
          tooltipContent={t("message_info")}
        >
          {t("new_message")}
        </SectionTitle>
      }
      size="lg"
      body={<Container>{formBody}</Container>}
      actions={
        <HpButton
          form="message-form-embedded"
          type="submit"
          squared
          slize="lg"
          disabled={disabled}
        >
          {t("send")}
        </HpButton>
      }
    />
  );
};

export default MessageForm;
